import { v4 as uuidv4 } from 'uuid';
import {
  AnswerDto,
  QuestionBundleDto,
  QuestionDto,
  QuestionnaireDto,
  QuestionnaireTemplateDto,
} from '../Types/QuestionnaireT';
import { ajaxActions } from './AjaxActions';
import { ToastS } from './ToastS';

const BASE_URL = process.env.REACT_APP_QUESTIONNAIRE_SERVICE_URL;
const BUNDLES_BASE_URL = `${BASE_URL}/questionBundles`;

const editBundle = async (bundleId: string, patch: Partial<QuestionBundleDto>): Promise<QuestionBundleDto | null> => {
  const res = await ajaxActions.patch(`${BUNDLES_BASE_URL}/${bundleId}`, patch);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const insertQuestion = async (bundleId: string, index?: number): Promise<QuestionBundleDto | null> => {
  const params = [];
  const questionId = uuidv4();
  if (index !== undefined) {
    params.push(`index=${index}`);
  }
  const res = await ajaxActions.put(`${BUNDLES_BASE_URL}/${bundleId}/${questionId}?${params.join('&')}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const editQuestion = async (questionId: string, patch: Partial<QuestionDto>): Promise<QuestionDto | null> => {
  const res = await ajaxActions.patch(`${BASE_URL}/questions/${questionId}`, patch);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const moveQuestion = async (
  bundleId: string,
  questionId: string,
  targetQuestionId: string,
  originBundleId?: string,
): Promise<QuestionnaireTemplateDto | QuestionnaireDto | null> => {
  const res = await ajaxActions.put(
    `${BUNDLES_BASE_URL}/${bundleId}/question/${questionId}/v2/moveTo/${targetQuestionId}${
      originBundleId ? `?originBundleId=${originBundleId}` : ''
    }`,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const duplicateQuestion = async (
  bundleId: string,
  questionId: string,
  index?: number,
): Promise<QuestionDto[] | null> => {
  const res = await ajaxActions.post(
    `${BUNDLES_BASE_URL}/${bundleId}/${questionId}/copy${index ? `?index=${index}` : ''}`,
  );
  if (res.status === 201) {
    ToastS.success('queston-duplicated', 'Frage dupliziert');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deleteQuestion = async (bundleId: string, questionId: string): Promise<boolean> => {
  const res = await ajaxActions.del(`${BUNDLES_BASE_URL}/${bundleId}/${questionId}`);
  if (res.ok) {
    ToastS.info('question-removed', 'Frage wurde entfernt');
    return true;
  }
  ToastS.generalError();
  return false;
};

const handleBundleChange = (template: QuestionnaireTemplateDto | QuestionnaireDto, patch: QuestionBundleDto): any => {
  const { questionBundles } = template;
  return {
    ...template,
    questionBundles: questionBundles.map((qb) => (qb.id === patch.id ? patch : qb)),
  };
};

const handleBundleDelete = (template: QuestionnaireTemplateDto | QuestionnaireDto, bundleId: string): any => {
  const { questionBundles } = template;
  return {
    ...template,
    questionBundles: questionBundles.filter((qb) => qb.id !== bundleId),
  };
};

const handleQuestionDuplicate = (
  template: QuestionnaireTemplateDto | QuestionnaireDto,
  bundleId: string,
  questions: QuestionDto[],
): any => {
  const { questionBundles } = template;
  return {
    ...template,
    questionBundles: questionBundles.map((qb) => {
      if (qb.id !== bundleId) return qb;
      return {
        ...qb,
        questions,
      };
    }),
  };
};

const handleQuestionChange = (template: QuestionnaireTemplateDto | QuestionnaireDto, question: QuestionDto): any => {
  const { questionBundles } = template;
  return {
    ...template,
    questionBundles: questionBundles.map((qb) => {
      const { questions } = qb;
      return {
        ...qb,
        questions: questions.map((q) => (q.id === question.id ? question : q)),
      };
    }),
  };
};

const handleQuestionDelete = (
  template: QuestionnaireTemplateDto | QuestionnaireDto,
  bundleId: string,
  questionId: string,
): any => {
  const { questionBundles } = template;
  return {
    ...template,
    questionBundles: questionBundles.map((qb) => {
      if (qb.id !== bundleId) return qb;
      const { questions } = qb;
      return {
        ...qb,
        questions: questions.filter((q) => q.id !== questionId),
      };
    }),
  };
};

const moveAnswer = async (
  questionId: string,
  answerId: string,
  targetAnswerId: string,
): Promise<AnswerDto[] | null> => {
  const res = await ajaxActions.patch(
    `${BASE_URL}/questions/${questionId}/v2/move-answer?answerId=${answerId}&targetAnswerId=${targetAnswerId}`,
    {},
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const insertAnswer = async (questionId: string, additionalAnswer?: boolean): Promise<AnswerDto | null> => {
  const answerId = uuidv4();
  const res = await ajaxActions.put(
    `${BASE_URL}/answers/${answerId}/${questionId}${additionalAnswer ? `?additionalAnswer=${additionalAnswer}` : ''}`,
  );
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const editAnswer = async (answerId: string, patch: Partial<AnswerDto>): Promise<AnswerDto | null> => {
  const res = await ajaxActions.patch(`${BASE_URL}/answers/${answerId}`, patch);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deleteAnswer = async (answerId: string, questionId: string): Promise<boolean> => {
  const res = await ajaxActions.del(`${BASE_URL}/answers/${answerId}/${questionId}`);
  if (res.ok) {
    return true;
  }
  ToastS.generalError();
  return false;
};

// eslint-disable-next-line import/prefer-default-export
export const QuestionBundleS = {
  editBundle,
  insertQuestion,
  editQuestion,
  moveQuestion,
  duplicateQuestion,
  deleteQuestion,
  insertAnswer,
  editAnswer,
  deleteAnswer,
  moveAnswer,
  handleBundleChange,
  handleBundleDelete,
  handleQuestionDuplicate,
  handleQuestionChange,
  handleQuestionDelete,
};
